import { useEffect, useState } from 'react'
import { sendEditingPresence, type PresenceEditor } from '@/lib/api'

const PING_INTERVAL_MS = 20_000

/**
 * Editing-presence beacon (Specs/draft-safety-spec.md Part 3).
 * Polling, not push — a heads-up, not a lock. Pauses while the tab is hidden;
 * server-side liveness expiry (45s) clears ghosts.
 */
export function useEditingPresence(
  sessionId: string | null,
  sectionId: string | null,
  enabled: boolean
): { editors: PresenceEditor[] } {
  const [editors, setEditors] = useState<PresenceEditor[]>([])

  useEffect(() => {
    if (!enabled || !sessionId || !sectionId) {
      setEditors([])
      return
    }

    let cancelled = false

    const ping = async () => {
      if (typeof document !== 'undefined' && document.visibilityState === 'hidden') return
      try {
        const res = await sendEditingPresence(sessionId, sectionId)
        if (!cancelled) setEditors(res.editors)
      } catch {
        // Presence is best-effort; never surface errors.
      }
    }

    void ping()
    const interval = setInterval(() => { void ping() }, PING_INTERVAL_MS)
    return () => {
      cancelled = true
      clearInterval(interval)
    }
  }, [enabled, sessionId, sectionId])

  return { editors }
}
